import React, { useCallback, useState } from 'react'

import { createLocatorProps } from 'contexts/locator'
import { SectionType } from 'shoplist-app/shopping/types/ShoppingPageType'

import { default as SectionComponent } from '../Shopping/components/Section'
import './renderSectionV3.scss'
import PageMessages from 'views/Shopping/components/PageMessages'
import { LOCATORS } from 'constants/automation'
import { Section } from 'types/graphql/fragments/Section'
import { LAYOUT_CONTAINER_TYPES } from 'lib/client-constants'

// Constants
const TERMS_KEYWORD = 'Terms'
const HONEY_TOUCHPOINT_ID = 'ppshopping_honey_join'

// Helper function to detect if a section contains terms content
const isTermsSection = (section: SectionType): boolean =>
  [
    section.htmlSubtitle?.content,
    section.title,
    section.subtitle,
    ...(section.items?.flatMap(item => [
      item.title,
      item.subtitle,
      item.description
    ]) || [])
  ].some(text => text?.includes(TERMS_KEYWORD))

type RenderSectionV3Props = {
  sections: SectionType[]
  layoutId: string
  adSlot?: React.ReactNode
}

const getHasOmniChannelWidget = (sections: SectionType[]) =>
  !!sections.find(
    section =>
      section.layout.containerType === LAYOUT_CONTAINER_TYPES.CAROUSEL ||
      section.layout.containerType === LAYOUT_CONTAINER_TYPES.LIST
  )

const getUpdatedVisibleSections = (
  sections: SectionType[],
  sectionId?: Section['sectionId']
) => {
  const filteredSections =
    sectionId !== undefined
      ? sections.filter(section => {
          return section.sectionId !== sectionId
        })
      : sections
  const hasOmniChannelWidget = getHasOmniChannelWidget(filteredSections)
  if (!hasOmniChannelWidget) {
    return []
  }
  return filteredSections
}

const RenderSectionV3 = ({
  layoutId,
  sections,
  adSlot
}: RenderSectionV3Props) => {
  // This returns as empty array if there's no widget section on the section array
  const initialVisibleSections = getUpdatedVisibleSections(sections)
  // If sections does not contain a widget section there's an error with the SPNS response
  const initialHasError = !getHasOmniChannelWidget(sections)
  const [visibleSections, setVisibleSections] = useState(initialVisibleSections)
  const [hasSectionWithErrors, setHasSectionWithErrors] =
    useState(initialHasError)

  const handleEmpty = useCallback((sectionId: SectionType['sectionId']) => {
    setVisibleSections(prevVisibleSections =>
      getUpdatedVisibleSections(prevVisibleSections, sectionId)
    )
  }, [])

  const handleError = useCallback((sectionId: SectionType['sectionId']) => {
    setHasSectionWithErrors(true)
    setVisibleSections(prevVisibleSections =>
      getUpdatedVisibleSections(prevVisibleSections, sectionId)
    )
  }, [])

  if (!visibleSections.length) {
    return <PageMessages hasNoDeals hasError={hasSectionWithErrors} />
  }

  // Reorder sections: honey first, then terms, then everything else
  const reorderedSections = [...visibleSections].sort((a, b) => {
    const aIsHoney =
      a.layout.containerType === LAYOUT_CONTAINER_TYPES.NONE &&
      a.touchpointId === HONEY_TOUCHPOINT_ID
    const bIsHoney =
      b.layout.containerType === LAYOUT_CONTAINER_TYPES.NONE &&
      b.touchpointId === HONEY_TOUCHPOINT_ID
    const aIsTerms = isTermsSection(a)
    const bIsTerms = isTermsSection(b)

    // Priority order: 1. Honey, 2. Terms, 3. Everything else

    // Honey sections come first (priority 1)
    if (aIsHoney && !bIsHoney) {
      return -1
    }
    if (!aIsHoney && bIsHoney) {
      return 1
    }

    // If both or neither are honey, check terms
    if (!aIsHoney && !bIsHoney) {
      // Terms sections come before other sections (priority 2)
      if (aIsTerms && !bIsTerms) {
        return -1
      }
      if (!aIsTerms && bIsTerms) {
        return 1
      }
    }

    // Keep original order for sections of same type
    return 0
  })

  return (
    <div
      {...createLocatorProps(LOCATORS.SECTIONS)}
      className="render-section-v3"
    >
      {adSlot && <div className="render-section-v3__ad-slot">{adSlot}</div>}
      {reorderedSections.map((section, index) => {
        if (isTermsSection(section)) {
          return (
            <div key={`section-${index}`} className="render-section-v3__terms">
              <SectionComponent
                layoutId={layoutId}
                section={section}
                onEmpty={handleEmpty}
                onError={handleError}
              />
            </div>
          )
        }

        return (
          <SectionComponent
            key={`section-${index}`}
            layoutId={layoutId}
            section={section}
            onEmpty={handleEmpty}
            onError={handleError}
          />
        )
      })}
    </div>
  )
}

export default RenderSectionV3
